import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import PhoneInput from "auth/PhoneInput";
import {useAuthenticatedUser} from "hooks/useAuth";

import useForm, {type FieldConfig} from "hooks/useForm";
import {useSetUnits, useUnits} from "hooks/useUnits";
import PaybillOptions from "pages/dashboard/PaybillOptions";
import {useCreatePostSTKMutation} from "pages/dashboard/api";
import type {Transaction} from "pages/messaging-account/transactions/api";
import {type Dispatch, type FC, type SetStateAction} from "react";
import {handleMutation} from "services/http/utils";
import {handleSuccess} from "services/notification/handlers";
import {validatePhone} from "utils/validators";
import {formatNumber, parseFormattedNumber} from "utils/values";

type WalletCardProps = {
  onClose: () => void;
  setData?: Dispatch<SetStateAction<Transaction[] | null>>;
};

const TopUpForm: FC<WalletCardProps> = ({onClose, setData}) => {
  const user = useAuthenticatedUser();

  const units = useUnits();
  const updateUnits = useSetUnits();

  const [initiateSTK, initiateSTKState] = useCreatePostSTKMutation();

  const initialFields: Record<string, FieldConfig<any>> = {
    amount: ["", () => ""],
    phone: [user.phone ?? "", validatePhone],
  };
  const onSuccess = (res: Transaction) => {
    updateUnits(formatNumber(parseFormattedNumber(units) + res.units));

    setData?.(prev => [res, ...(prev ?? [])]);

    handleSuccess(
      `M-PESA STK Push initiated successfully for ${res.amount}/=. Please check your phone to authorize the payment.`,
    );

    onClose();
  };

  const {fields, handleSubmit, resetFields, setFieldError} = useForm({
    fields: initialFields,
    onSubmit: async values => {
      if (!values.amount || values.amount <= 0) {
        setFieldError("amount", "Please enter a valid positive amount.");
        return;
      }

      const payload = {amount: values.amount, phone: values.phone || undefined};

      await handleMutation(initiateSTK, payload, undefined, undefined, {
        onSuccess: res => onSuccess(res),
      });
    },
  });

  const handleClose = () => {
    onClose();
    resetFields();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Wallet Details
        </Typography>

        <Box component="table" sx={{width: "100%", mb: 2, fontSize: 14}}>
          <tbody>
            <tr>
              <td>Balance [units]</td>
              <td style={{paddingLeft: 8}}>{units}</td>
            </tr>
          </tbody>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{display: "flex", flexDirection: "column", gap: 1}}>
          <PhoneInput {...fields.phone} size="small" />

          <TextField
            size="small"
            label="Amount"
            placeholder="Enter amount"
            value={fields.amount.value}
            onChange={e => {
              const val = Number(e.target.value);
              fields.amount.onChange(val > 0 ? val : "");
            }}
            required
            type="number"
          />

          <Box mt={2} display="flex" justifyContent="space-between">
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={initiateSTKState.loading}>
              Lipa Na M-PESA Online
            </Button>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
          </Box>
        </Box>

        <Divider sx={{my: 2}} />
        <PaybillOptions />
      </CardContent>
    </Card>
  );
};

export default TopUpForm;
